import { createFileRoute, ErrorComponent } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Calendar, Clock, Monitor } from "lucide-react";
import { getSessionDataOptions } from "~/lib/functions/summit";
import { Card, CardContent } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { AuthorCard } from "~/components/ui/author-card";
import { SummitRegistration } from "~/components/summit/summit-registration";
import { MarkdownContent } from "~/components/markdown-content";
import { WaveBackground } from "~/components/ui/wave-background";
import { createBrandMeta } from "~/lib/shared/meta";

export const Route = createFileRoute(
  "/_public/summit/$summitId/session/$sessionId"
)({
  loader: ({ context, params }) => {
    return context.queryClient.ensureQueryData(
      getSessionDataOptions(params.sessionId)
    );
  },
  head: ({ loaderData, params }) => {
    const { session, summit } = loaderData || {};

    if (!session) {
      return createBrandMeta({
        title: "Summit Session",
        description:
          "Join us for an exclusive summit session with industry experts.",
        ogType: "website",
      });
    }

    // Generate description combining session and speaker info
    const speakerInfo = session.speaker.title
      ? `${session.speaker.name}, ${session.speaker.title}`
      : session.speaker.name;

    const description = session.fullDescription || session.description;
    const enhancedDescription = `${description} Join ${speakerInfo} for this ${formatSessionType(session.sessionFormat)} session.`;

    // Generate keywords from session data
    const keywords = [
      "summit",
      "session",
      "coaching",
      session.sessionFormat || "presentation",
      session.speaker.name,
      session.speaker.title || "",
      ...session.topics,
    ]
      .filter(Boolean)
      .filter((keyword): keyword is string => typeof keyword === "string");

    // Session URL
    const sessionUrl = `https://dreamcoachmatch.com/summit/${params.summitId}/session/${params.sessionId}`;

    // Use custom session social image
    const ogImage = `https://dreamcoachmatch.com/summit/${params.summitId}/session/${params.sessionId}/social-image.svg`;

    const metaTags = createBrandMeta({
      title: session.title,
      description: enhancedDescription,
      ogTitle: `${session.title} | ${summit?.title || "Summit"}`,
      ogDescription: description,
      ogImage,
      ogType: "website",
      ogUrl: sessionUrl,
      twitterCard: "summary_large_image",
      twitterTitle: session.title,
      twitterDescription: description,
      twitterImage: ogImage,
      canonical: sessionUrl,
      keywords,
      author: session.speaker.name,
    });

    // Create Event structured data
    const eventStructuredData = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: session.title,
      description: description,
      startDate: new Date(
        `${session.date.toISOString().split("T")[0]}T${convertTimeToISO(session.startTime)}`
      ).toISOString(),
      endDate: new Date(
        `${session.date.toISOString().split("T")[0]}T${convertTimeToISO(session.endTime)}`
      ).toISOString(),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      location: {
        "@type": "VirtualLocation",
        url: sessionUrl,
      },
      performer: {
        "@type": "Person",
        name: session.speaker.name,
        jobTitle: session.speaker.title,
        image: session.speaker.avatarUrl,
        ...(session.speaker.bio && { description: session.speaker.bio }),
        ...(session.speaker.linkedInUrl && {
          sameAs: [
            session.speaker.linkedInUrl,
            session.speaker.twitterUrl,
            session.speaker.websiteUrl,
          ].filter(Boolean),
        }),
      },
      organizer: {
        "@type": "Organization",
        name: "Dream Coach Match",
        url: "https://dreamcoachmatch.com",
      },
      ...(session.topics.length > 0 && {
        about: session.topics.map((topic) => ({
          "@type": "Thing",
          name: topic,
        })),
      }),
    };

    return {
      ...metaTags,
      scripts: [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(eventStructuredData),
        },
      ],
    };
  },
  component: SessionPage,
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
});

const formatSessionType = (format?: string) => {
  if (!format) return "Session";
  return format.charAt(0).toUpperCase() + format.slice(1);
};

const convertTimeToISO = (timeString: string): string => {
  // Convert "10:00 AM" or "2:30 PM" format to "10:00:00" or "14:30:00"
  const [time, period] = timeString.split(" ");
  const [hours, minutes] = time.split(":").map(Number);

  let hour24 = hours;
  if (period === "PM" && hours !== 12) {
    hour24 += 12;
  } else if (period === "AM" && hours === 12) {
    hour24 = 0;
  }

  return `${hour24.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
};

function SessionPage() {
  const { sessionId } = Route.useParams();
  const { data } = useSuspenseQuery(getSessionDataOptions(sessionId));
  const { session } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative">
        <WaveBackground />

        {/* Overlay for better contrast */}
        <div className="absolute inset-0 bg-black/20" />

        <div className="mx-auto max-w-4xl px-6 py-12 z-10 relative">
          {/* Session metadata */}
          <div className="mb-6 flex flex-wrap items-center gap-3 z-10">
            {session.sessionFormat && (
              <Badge variant="secondary" className="gap-1.5">
                <Monitor className="h-3 w-3" />
                {formatSessionType(session.sessionFormat)}
              </Badge>
            )}
            <div className="flex items-center gap-1.5 text-sm text-white/80">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {session.startTime} - {session.endTime} {session.timezone}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-white/80">
              <Calendar className="h-3.5 w-3.5" />
              <span>{format(session.date, "EEEE, MMMM d, yyyy")}</span>
            </div>
          </div>

          {/* Session title */}
          <div className="mt-12 mb-8 flex flex-row justify-between items-center gap-4">
            <h1 className="font-heading max-w-xl text-balance text-4xl md:text-5xl tracking-tight text-white lg:text-6xl">
              {session.title}
            </h1>
            <a href="#register">
              <Button variant="secondary" className="shadow-lg">
                Register
              </Button>
            </a>
          </div>

          {/* Speaker section */}
          <Card className="mb-8">
            <CardContent>
              <AuthorCard
                author={session.speaker}
                variant="large"
                showBio={true}
                showSocialLinks={true}
                titleLevel="h2"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content Section */}
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="grid gap-12 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="space-y-8">
              {/* Session description */}
              <div>
                <h3 className="mb-4 text-2xl font-semibold text-gray-900">
                  About This Session
                </h3>
                {session.fullDescription && (
                  <MarkdownContent
                    content={session.fullDescription}
                    className="prose"
                  />
                )}
              </div>

              {/* Topics covered */}
              {session.topics.length > 0 && (
                <div>
                  <h3 className="mb-4 text-2xl font-semibold text-gray-900">
                    Topics Covered
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {session.topics.map((topic) => (
                      <Badge key={topic} variant="secondary" size="lg">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Card>
                <CardContent>
                  <a id="register" className="anchor -mt-12" />
                  <SummitRegistration />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sidebar - Registration CTA */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              {/* Additional session info */}
              <Card className="mt-6">
                <CardContent>
                  <h4 className="mb-4 font-semibold text-gray-900">
                    Session Details
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Format</span>
                      <span className="font-medium">
                        {formatSessionType(session.sessionFormat)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-medium">
                        {session.startTime} - {session.endTime}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Date</span>
                      <span className="font-medium">
                        {format(session.date, "MMM d, yyyy")}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
